import { type FC, type PropsWithChildren, useCallback } from "react";

import { CopyIcon, WarningAltIcon } from "@moment/icons";
import { makeToast } from "@moment/ui";

import { getAdapterIcon, getAdapterName } from "../AtlasAdapterNameIcon";
import { ExpandedSection } from "../ExpandedSection";

export type ValueErrorProps = PropsWithChildren<{
	message: string;
	additionalInfo?: React.ReactNode;
	collapsedByDefault?: boolean;
	originalError?: Error & {
		atlasErrorId?: string;
		adapterGuess?: string;
		documentationUrl?: string;
	};
}>;

const errorIconProps = { className: "text-negative", size: 16 } as const;

export const ValueError: FC<ValueErrorProps> = ({
	message,
	additionalInfo,
	collapsedByDefault = true,
	originalError,
}) => {
	let icon = <WarningAltIcon {...errorIconProps} />;
	let shortMessage: string | undefined;
	let longMessage: JSX.Element | string = message;
	const atlasErrorId: string | undefined = originalError?.atlasErrorId;
	const adapterGuess: string | undefined = originalError?.adapterGuess;
	let showAdapterUnresponsiveError = false;

	switch (atlasErrorId) {
		case "API_NoMatchingFollowerError":
		case "API_NoMatchingInstanceError": {
			showAdapterUnresponsiveError = true;

			const adapterName =
				adapterGuess !== undefined ? getAdapterName(adapterGuess) : undefined;
			shortMessage = adapterName
				? `${adapterName} integration unresponsive`
				: `Integration unresponsive`;
			longMessage = (
				<>
					This component requires the{" "}
					<span className="font-bold">{adapterName} Atlas Adapter</span>, but it is not
					responding. Usually the adapter is not properly installed, or the request is
					malformed.{" "}
					{originalError?.documentationUrl && (
						<a rel="noreferrer" href={originalError?.documentationUrl} target="_blank">
							Learn how to troubleshoot this issue
						</a>
					)}
					.
				</>
			);

			const Icon = getAdapterIcon(adapterGuess);
			if (adapterGuess && Icon) {
				icon = <Icon {...errorIconProps} />;
			}

			break;
		}
	}

	const handleCopyToClipboard = useCallback(async () => {
		let textToCopy: string;
		if (typeof longMessage === "string") {
			textToCopy = longMessage;
		} else {
			// Convert the JSX.Element to a string representation
			textToCopy = JSON.stringify(longMessage);
		}

		if (shortMessage) {
			// If there is a short message, prepend it to the text to copy
			textToCopy = `${shortMessage}: ${textToCopy}`;
		}

		if (typeof additionalInfo === "string" && additionalInfo && additionalInfo?.length > 0) {
			textToCopy = `${textToCopy}\n\n${additionalInfo}`;
		}

		await navigator.clipboard
			.writeText(textToCopy)
			.then(() => {
				makeToast({
					variant: "success",
					message: "Copied error message to clipboard",
				});
			})
			.catch(() => {
				makeToast({
					variant: "error",
					message: "Failed to copy error message",
				});
			});
	}, [longMessage, shortMessage, additionalInfo]);

	return (
		<div className="flex flex-col bg-secondary p-2 text-sm">
			<ExpandedSection
				collapsedByDefault={collapsedByDefault}
				allowLabelClick={false}
				headerClassName="space-x-2"
				contentClassName="ml-6"
				variant="negative"
				label={
					<div className="my-1 flex items-start space-x-2 px-1 w-full">
						<div className="mt-0.5">{icon}</div>
						{showAdapterUnresponsiveError ? (
							<div className="flex flex-col space-y-1 flex-1">
								<h3 className="text-sm font-bold text-negative">{shortMessage}</h3>
								<div>{longMessage}</div>
							</div>
						) : (
							<div className="whitespace-pre-wrap break-all font-mono text-negative flex-1">
								{longMessage}
							</div>
						)}
						<button onClick={handleCopyToClipboard}>
							<CopyIcon className="text-secondary" />
						</button>
					</div>
				}
			>
				{/* @ts-ignore -- additionalInfo is a ReactNode */}
				{typeof additionalInfo === "string" &&
				additionalInfo &&
				additionalInfo?.length > 0 ? (
					<pre
						className="!mt-1 !overflow-y-hidden whitespace-pre-wrap !bg-transparent !p-0"
						style={{ wordBreak: "break-all" }}
					>
						{additionalInfo}
					</pre>
				) : (
					additionalInfo
				)}
			</ExpandedSection>
		</div>
	);
};
